import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Typography,
    Box
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';

// Hook personalizado para validação do token
export const useTokenValidation = () => {
    const [showExpiredDialog, setShowExpiredDialog] = useState(false);
    const [countdown, setCountdown] = useState(15);
    const triggerExpiredDialog = useCallback(() => {
        setShowExpiredDialog(true);
    }, []);
    const [nomeUsuario, setNomeUsuario] = useState('Usuário');

    // Função para verificar se o token está válido/ativo
    const isTokenValid = useCallback((token) => {
        if (!token) return false;

        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000; // Tempo atual em segundos

            // Verifica se o token tem data de expiração e se ainda é válido
            if (decoded.exp && decoded.exp < currentTime) {
                return false; // Token expirado
            }

            return true; // Token válido
        } catch (error) {
            console.error('Erro ao validar o token:', error);
            return false; // Token inválido
        }
    }, []);

    // Função para redirecionar para a home
    const redirectToHome = useCallback(() => {
        // Remove o token inválido do localStorage
        localStorage.removeItem('jwtToken');
        // Redireciona para a página inicial
        window.location.href = '/';
    }, []);

    // Função para validar o token
    const validateToken = useCallback(() => {
        const token = localStorage.getItem('jwtToken');

        if (token && isTokenValid(token)) {
            // Token válido - decodifica e obtém informações do usuário
            try {
                const decoded = jwtDecode(token);
                setNomeUsuario(decoded.nome || 'Usuário');
                console.log('Token válido. Usuário:', decoded.nome || 'Usuário');
            } catch (error) {
                console.error('Erro ao decodificar o token:', error);
                setShowExpiredDialog(true);
            }
        } else {
            // Token inválido, expirado ou não existe
            console.log('Token inválido ou expirado');
            setShowExpiredDialog(true);
        }
    }, [isTokenValid]);

    // Efeito para validação inicial
    useEffect(() => {
        validateToken();
    }, [validateToken]);

    useEffect(() => {
        window.addEventListener('tokenExpired', triggerExpiredDialog);

        return () => {
            window.removeEventListener('tokenExpired', triggerExpiredDialog);
        };
    }, [triggerExpiredDialog]);

    // Efeito para verificação periódica do token (a cada 5 minutos)
    useEffect(() => {
        const interval = setInterval(() => {
            const currentToken = localStorage.getItem('jwtToken');
            if (!currentToken || !isTokenValid(currentToken)) {
                console.log('Token expirou durante a sessão');
                setShowExpiredDialog(true);
            }
        }, 5 * 60 * 1000); // 5 minutos

        return () => clearInterval(interval);
    }, [isTokenValid]);

    // Efeito para contagem regressiva
    useEffect(() => {
        let countdownInterval;

        if (showExpiredDialog) {
            countdownInterval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        redirectToHome();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (countdownInterval) {
                clearInterval(countdownInterval);
            }
        };
    }, [showExpiredDialog, redirectToHome]);

    // Reset countdown quando o dialog é fechado
    useEffect(() => {
        if (!showExpiredDialog) {
            setCountdown(15);
        }
    }, [showExpiredDialog]);

    return {
        nomeUsuario,
        showExpiredDialog,
        countdown,
        redirectToHome
    };
};

// Componente do Dialog de Token Expirado
export const TokenExpiredDialog = ({ open, countdown, onRedirect }) => {
    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            aria-labelledby="token-expired-dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minWidth: { xs: 300, sm: 400 },
                    maxWidth: 500,
                    mx: 2, // Margem lateral em mobile
                }
            }}
        >
            <DialogTitle
                id="token-expired-dialog-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1
                }}
            >
                <Warning color="warning" />
                <Typography variant="h6" component="span">
                    Sessão Expirada
                </Typography>
            </DialogTitle>

            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Sua sessão expirou. Você será redirecionado para a página inicial.
                </DialogContentText>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 1
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 'bold', textAlign: 'center' }}
                    >
                        Redirecionamento automático em {countdown} segundos...
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onRedirect}
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                >
                    OK - Ir para Login
                </Button>
            </DialogActions>
        </Dialog>
    );
};
